import { useQuery } from '@apollo/client';
import React, { PropsWithChildren, useMemo, useState } from 'react';
import { useAppSelector } from '../../../redux/hooks';
import { useUserSettingsSelector } from '../../../redux/selectorHooks';
import { SEARCH_TAGS } from '../../../utils/apollo/entitySelection/query';
import {
  ISearchTagsQuery,
  ISearchTagsQueryVariables,
  ITagOptionInput,
} from '../../../utils/apollo/graphqlSchema';
import { ISearchTagsState } from '../../Builder/EntitySelection/types';
import { createSearchTagsState } from '../../Builder/EntitySelection/utils';
import { useDndEntityInternalContext } from '../context';
import DndDroppableZone from '../../DndEntity/DndDroppableZone';
import { getDndActiveItemData } from '../utils';
import {
  IBreakdownDropzoneProperties,
  IFieldEntityDropzoneDataType,
  IPropertyEntityDropzoneDataType,
  ISignificanceDropzoneDataType,
} from '../validation';
import { EntityDropzoneType } from '../../QuickAccessDictionary/DictionaryTabs/types';
import { DefaultView } from '../../../utils/userSettings';

interface IProps {
  dropzoneId: string;
  tags: ITagOptionInput[];
  data:
    | IPropertyEntityDropzoneDataType
    | IBreakdownDropzoneProperties
    | IFieldEntityDropzoneDataType
    | ISignificanceDropzoneDataType;
  hasError?: boolean;
  shouldFitContent?: boolean;
  customMessage?: string;
  hideOnDisabled?: boolean;
}

const FieldDroppableZone = ({
  dropzoneId,
  tags,
  data,
  children,
  customMessage,
  hasError = false,
  shouldFitContent = false,
  hideOnDisabled = false,
}: PropsWithChildren<IProps>) => {
  const defaultView = useUserSettingsSelector('defaultView');
  const schemaId = useAppSelector(({ builder }) => builder.activeSchemaId);
  const [searchTagState, setSearchTagState] = useState<ISearchTagsState>();

  const { activeItem } = useDndEntityInternalContext();

  const { loading } = useQuery<ISearchTagsQuery, ISearchTagsQueryVariables>(
    SEARCH_TAGS,
    {
      variables: {
        schemaId,
        searchTerm: '',
        tags,
        managedOnly: defaultView === DefaultView.ManagedView,
      },
      skip: !schemaId,
      onCompleted: (data) => {
        setSearchTagState(createSearchTagsState(data.searchTags));
      },
    },
  );

  // some fields may not be allowed to drop onto the property input.
  // we need to check the dragged field, to see if it's within the custom property searchTags query
  // if the dragged field is within these results, then it is legal to drop. Otherwise do not allow drop
  const isFieldDisabled = useMemo(() => {
    const data = getDndActiveItemData(activeItem);
    const searchTagsChildren = searchTagState?.root.children;

    if (data && searchTagsChildren?.length) {
      return !searchTagsChildren.some(
        (tagBranch) =>
          tagBranch.__typename === 'TagBranch' &&
          tagBranch.children.some(
            (tag) => tag.__typename === 'TagLeafField' && tag.key === data.key,
          ),
      );
    }

    return false;
  }, [activeItem, searchTagState]);

  // this prop will determine whether we should return children or not on disabled.
  // there are some field dropzones where we render a horizontal dropzone for fields.
  // however, if it's disabled, we should not render those dropzones
  if (hideOnDisabled && (isFieldDisabled || loading)) {
    return null;
  }

  return (
    <DndDroppableZone
      accepts={[EntityDropzoneType.Fields]}
      id={dropzoneId}
      data={data}
      shouldFitContent={shouldFitContent}
      customMessage={customMessage}
      isDisabled={isFieldDisabled}
      hasError={isFieldDisabled || hasError}
    >
      {children}
    </DndDroppableZone>
  );
};

export default FieldDroppableZone;
